import type { SiteDto, SkuDto } from '@pl/domain';
import type { ReactNode } from 'react';
import { ApiError } from '../lib/api';

const LIGHT = new Set(['#f4f4f2', '#ffffff', '#fff', '#e8eef2', '#e8dcc0']);

export function Swatch({ hex, name, size = 'md' }: { hex: string | null; name: string | null; size?: 'sm' | 'md' | 'lg' | 'xl' }) {
  const outline = !hex || LIGHT.has(hex.toLowerCase());
  const sizeClass = size === 'lg' ? ' lg' : size === 'xl' ? ' xl' : '';
  return <span className={`sw${sizeClass}${outline ? ' outline' : ''}`} style={{ background: hex ?? 'transparent' }} title={name ?? 'No color'} aria-hidden="true" />;
}

export function SkuTag({ sku, short = false, withCode = false }: { sku: Pick<SkuDto, 'code' | 'name' | 'colourName' | 'colourHex'> | null | undefined; short?: boolean; withCode?: boolean }) {
  if (!sku)
    return (
      <span className="sku">
        <Swatch hex={null} name={null} />
        <b>Blank sticker</b>
      </span>
    );
  const name = short ? shortName(sku.name) : sku.name;
  return (
    <span className="sku">
      <Swatch hex={sku.colourHex} name={sku.colourName} />
      <b>{name}</b>
      {sku.colourName && <span className="clr">{sku.colourName}</span>}
      {withCode && <span className="code">{sku.code}</span>}
    </span>
  );
}

/** "CAT6 CMR 1000ft" → "C6 CMR"; "CAT5E CMR 1000ft" → "C5E CMR"; "CAT6A CMP Shielded 1000ft" → "C6A CMP". */
export function shortName(name: string): string {
  const m = name.match(/^CAT(\w+)\s+(\w+)/i);
  return m ? `C${m[1]!.toUpperCase()} ${m[2]!.toUpperCase()}` : name;
}

export function SiteTag({ site }: { site: SiteDto | undefined }) {
  if (!site) return null;
  const variant = site.shortCode === 'CA' ? '' : site.shortCode === 'MD' ? ' md' : ' kc';
  return (
    <span className={`site-tag${variant}`} title={site.name}>
      {site.shortCode}
    </span>
  );
}

export function Chip({ tone, dot, children }: { tone?: 'ok' | 'warn' | 'bad' | 'accent' | 'ink'; dot?: boolean; children: ReactNode }) {
  return (
    <span className={`chip${tone ? ` ${tone}` : ''}`}>
      {dot && <span className="dot" />}
      {children}
    </span>
  );
}

export function Bar({ value, max, tone }: { value: number; max: number; tone?: 'ok' | 'warn' | 'accent' }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className={`bar${tone ? ` ${tone}` : ''}`} role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={max}>
      <i style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Empty({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return (
    <div className="empty">
      <b>{title}</b>
      {description && <p>{description}</p>}
      {action}
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  const msg = error instanceof ApiError || error instanceof Error ? error.message : 'Something went wrong';
  return (
    <p role="alert" className="error-note">
      {msg}
    </p>
  );
}

/** The part of a query result RefetchNote reads. Every `useQuery` result satisfies it. */
export interface Refetchable {
  isRefetchError: boolean;
  error: unknown;
  refetch: () => unknown;
}

/**
 * A query that already had data on screen, and then failed to refresh it.
 *
 * TanStack Query keeps the data in that case and still reports `isError`, so a page gated on
 * `isError` replaced a working screen with a bare error over one dropped request — the dashboard,
 * which refreshes stock every 20 seconds, went blank until someone pressed Try again, and the ship
 * screen lost its scanner mid-order. The rule: a whole-page error is only for a query with nothing
 * to show (`isLoadingError`). A failed refresh keeps the content and says so here, beside it.
 */
export function RefetchNote({ queries, className }: { queries: Refetchable[]; className?: string }) {
  const failed = queries.filter((q) => q.isRefetchError);
  if (failed.length === 0) return null;
  const error = failed[0]!.error;
  const msg = error instanceof Error ? error.message : 'Something went wrong';
  return (
    <div className={`refetch-note${className ? ` ${className}` : ''}`}>
      <p role="alert" className="error-note">
        Couldn’t refresh. {msg}
      </p>
      <button type="button" className="btn sm" onClick={() => failed.forEach((q) => void q.refetch())}>
        Try again
      </button>
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return <span className="spinner" role="status" aria-label={label} />;
}
